import React, { useContext } from 'react';
import { Close, Email, Phone, Person, Business, LocalOffer, Payment, PersonPin, LocationOn, Category, Public, Info } from '@mui/icons-material';
import { Typography, Chip } from '@mui/material';
import DrawerContext from '../../../context/DrawerContext';
import "../../../styles/Drawer.scss";
import "../../../Pages/CouponLeads/CouponLeads.scss";

const ViewCouponLeadDrawer = ({ drawerDetails }) => {
  const drawerCtx = useContext(DrawerContext);

  const coupon = drawerDetails;
  const advertising = coupon?.advertising || {};
  const locations = coupon?.locations || [];
  const categories = coupon?.categories || [];
  const countries = coupon?.countries || [];

  const formatDate = (dateString) => {
    if (!dateString) return null;
    try {
      return new Date(dateString).toLocaleDateString('en-GB', { 
        day: 'numeric', 
        month: 'long', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (amount, unit) => {
    if (!amount) return null;
    return `${unit || 'INR'} ${parseFloat(amount).toFixed(2)}`;
  };

  const formatDeal = () => {
    const deal = coupon?.deal;
    const dealItem = coupon?.deal_item;
    const dealUnit = (coupon?.deal_unit || '').toUpperCase();
    if (!deal) return null;
    if (dealUnit === 'PER') {
      return `${deal}% Off`;
    }
    if (dealUnit === 'FREE') {
      const parts = [deal, dealItem, dealUnit].filter(Boolean);
      return parts.join(' ');
    }
    return `${deal} ${dealUnit} Off`;
  };

  const pipeToList = (str) => {
    if (!str || typeof str !== 'string') return [];
    return str.split('|').map((s) => s.trim()).filter(Boolean);
  };

  const renderStatusBadge = (status, type = 'status') => {
    if (type === 'status' && status === 'draft') {
      return <div className="status-draft">Draft</div>;
    }
    
    if (type === 'payment_status') {
      const paymentStatus = status;
      
      if (paymentStatus === 'completed') {
        return <div className="status-active">Paid</div>;
      }
      
      if (paymentStatus === 'pending') {
        return <div className="status-pending">Pending</div>;
      }
      
      if (paymentStatus === 'failed') {
        return <div className="status-deleted">Failed</div>;
      }
      
      if (paymentStatus === 'cancelled') {
        return <div className="status-suspended">Cancelled</div>;
      }
      
      return paymentStatus ? <div className="status-unknown">{paymentStatus}</div> : null;
    }
    
    return null;
  };

  const InfoRow = ({ icon: Icon, label, value, link = null, isBadge = false, badgeComponent = null, hideIfEmpty = false, asList = false }) => {
    if (hideIfEmpty && (!value || value === 'N/A')) return null;
    if (asList) {
      const items = pipeToList(value);
      if (hideIfEmpty && items.length === 0) return null;
      return (
        <div className="info-row">
          <div className="info-row-content info-row-content-list">
            {Icon && <Icon className="info-icon" />}
            <Typography variant="body2" className="info-label">
              {label}:
            </Typography>
            <div className="info-value">
              {items.length > 0 ? (
                <ul className="info-list">
                  {items.map((item, i) => (
                    <li key={i}>
                      <Typography variant="body2" className="info-text">
                        {item}
                      </Typography>
                    </li>
                  ))}
                </ul>
              ) : (
                <Typography variant="body2" className="info-placeholder">
                  Not provided
                </Typography>
              )}
            </div>
          </div>
        </div>
      );
    }
    
    const displayValue = value && value !== 'N/A' ? value : null;
    const isBold = label.includes('Code') || label.includes('Cost');
    
    return (
      <div className="info-row">
        <div className="info-row-content">
          {Icon && <Icon className="info-icon" />}
          <Typography variant="body2" className="info-label">
            {label}:
          </Typography>
          <div className="info-value">
            {isBadge && badgeComponent ? (
              badgeComponent
            ) : displayValue ? (
              link ? (
                <a 
                  href={link} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="info-link"
                >
                  {displayValue}
                </a>
              ) : (
                <Typography variant="body2" className={`info-text ${isBold ? 'bold' : ''}`}>
                  {displayValue}
                </Typography>
              )
            ) : (
              <Typography variant="body2" className="info-placeholder">
                Not provided
              </Typography>
            )}
          </div>
        </div>
      </div>
    );
  };

  const SectionHeader = ({ icon: Icon, title, count = null }) => (
    <div className="section-header">
      {Icon && <Icon className="section-icon" />}
      <Typography variant="h6" className="section-title">
        {title} {count !== null && `(${count})`}
      </Typography>
    </div>
  );

  return (
    <div className="coupon-lead-drawer">
      <div className="details-section">
        <header>
          <h1>Coupon Lead Details</h1>
          <Close onClick={() => drawerCtx.closeDrawer()} className="close-icon" />
        </header>

        <section>
          {/* Company Information */}
          <div className="details drawer-section">
            <SectionHeader icon={Business} title="Company Information" />
            <div className="card drawer-card">
              <div className="row">
                <div className="company-header">
                  <div 
                    className="company-logo-wrap" 
                    style={advertising.background_color ? { backgroundColor: advertising.background_color } : undefined}
                  >
                    {advertising.logo_md || advertising.logo_sm ? (
                      <img 
                        src={advertising.logo_md || advertising.logo_sm} 
                        alt="Company Logo" 
                        className="company-logo"
                      />
                    ) : (
                      <div className="company-logo-placeholder">
                        {(advertising.companyName || advertising.company_name) ? (advertising.companyName || advertising.company_name).charAt(0).toUpperCase() : 'N'}
                      </div>
                    )}
                  </div>
                  <Typography variant="h6" className="company-name">
                    {advertising.companyName || advertising.company_name || 'N/A'}
                  </Typography>
                </div>
              </div>
              
              <InfoRow 
                icon={Email} 
                label="Email" 
                value={advertising.email_address}
                link={advertising.email_address ? `mailto:${advertising.email_address}` : null}
              />
              <InfoRow 
                icon={Phone} 
                label="Phone" 
                value={advertising.phone_number}
                link={advertising.phone_number ? `tel:${advertising.phone_number}` : null}
              />
              <InfoRow 
                icon={Person} 
                label="Contact Person" 
                value={advertising.contact_person}
              />
            </div>
          </div>

          {/* Coupon Details */}
          <div className="details drawer-section">
            <SectionHeader icon={LocalOffer} title="Coupon Details" />
            <div className="card drawer-card">
              <InfoRow 
                label="Coupon Code" 
                value={coupon.coupon}
              />
              <InfoRow 
                label="Deal" 
                value={formatDeal()}
              />
              <InfoRow 
                label="Description" 
                value={coupon.description}
                hideIfEmpty={true}
              />
              <InfoRow 
                label="Terms" 
                value={coupon.terms}
                hideIfEmpty={true}
                asList={true}
              />
              <InfoRow 
                label="Instructions" 
                value={coupon.instructions}
                hideIfEmpty={true}
                asList={true}
              />
              <InfoRow 
                label="Duration" 
                value={coupon.duration ? `${coupon.duration} ${coupon.duration_type || 'days'}` : null}
              />
              <InfoRow 
                label="Online Only" 
                value={coupon.online_only === 'yes' ? 'Yes' : coupon.online_only === 'no' ? 'No' : null}
              />
              {coupon.website && (
                <InfoRow 
                  label="Website" 
                  value={coupon.website}
                  link={coupon.website}
                />
              )}
            </div>
          </div>

          {/* Payment Information */}
          <div className="details drawer-section">
            <SectionHeader icon={Payment} title="Payment Information" />
            <div className="card drawer-card">
              <InfoRow 
                label="Cost" 
                value={coupon.cost != null ? parseFloat(coupon.cost).toFixed(2) : null}
              />
              <InfoRow 
                label="Payment Status" 
                value={coupon.payment_status}
                isBadge={true}
                badgeComponent={renderStatusBadge(coupon.payment_status, 'payment_status')}
              />
              {coupon.payment_date && (
                <InfoRow 
                  label="Payment Date" 
                  value={formatDate(coupon.payment_date)}
                />
              )}
              {coupon.payment_id && (
                <InfoRow 
                  label="Payment ID" 
                  value={coupon.payment_id}
                />
              )}
              {coupon.pf_payment_id && (
                <InfoRow 
                  label="PF Payment ID" 
                  value={coupon.pf_payment_id}
                />
              )}
            </div>
          </div>

          {/* Customer Information */}
          <div className="details drawer-section">
            <SectionHeader icon={PersonPin} title="Customer Information" />
            <div className="card drawer-card">
              <InfoRow 
                icon={Person}
                label="Name" 
                value={coupon.customer_name}
              />
              <InfoRow 
                icon={Email}
                label="Email" 
                value={coupon.customer_email}
                link={coupon.customer_email ? `mailto:${coupon.customer_email}` : null}
              />
            </div>
          </div>

          {/* Agent Information */}
          <div className="details drawer-section">
            <SectionHeader icon={Person} title="Agent Information" />
            <div className="card drawer-card">
              {coupon.affiliate ? (
                <>
                  <InfoRow
                    label="Agent Name"
                    value={coupon.affiliate.name}
                  />
                  <InfoRow
                    label="Affiliate Code"
                    value={coupon.affiliate.code}
                  />
                  {coupon.affiliate.agent_fee != null && (
                    <InfoRow
                      label="Points awarded"
                      value={`${parseFloat(coupon.affiliate.agent_fee).toFixed(2)} points`}
                    />
                  )}
                </>
              ) : (
                <InfoRow
                  label="Agent"
                  value="No agent associated with this coupon."
                />
              )}
            </div>
          </div>

          {/* Locations */}
          {locations.length > 0 && (
            <div className="details drawer-section">
              <SectionHeader icon={LocationOn} title="Locations" count={locations.length} />
              <div className="card drawer-card">
                {locations.map((location, index) => {
                  const requiresManualSync = location.manualSync === "yes";
                  return (
                    <div 
                      key={location.id || index} 
                      className={`${index < locations.length - 1 ? "location-item" : "location-item"} ${requiresManualSync ? "manual-sync-disabled" : ""}`} 
                      style={{
                        ...(index === locations.length - 1 ? { borderBottom: 'none', paddingBottom: 0, marginBottom: 0 } : {}),
                        opacity: requiresManualSync ? 0.5 : 1,
                        backgroundColor: requiresManualSync ? "#f5f5f5" : "transparent"
                      }}
                    >
                      <Typography variant="body2" className="location-title">
                        {location.title || 'N/A'}
                      </Typography>
                      <Typography variant="body2" className="location-address">
                        {location.address || 'N/A'}
                      </Typography>
                      {location.latitude && location.longitude && (
                        <Typography variant="body2" className="location-coords">
                          {location.latitude}, {location.longitude}
                        </Typography>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Categories */}
          {categories.length > 0 && (
            <div className="details drawer-section">
              <SectionHeader icon={Category} title="Categories" count={categories.length} />
              <div className="card drawer-card">
                <div className="chip-container">
                  {categories.map((category, index) => (
                    <Chip
                      key={category.id || index}
                      label={`${category.title || 'N/A'} ${category.parent > 0 ? '(Sub)' : '(Main)'}`}
                      size="small"
                      sx={{
                        backgroundColor: category.parent > 0 ? '#e3f2fd' : '#f3e5f5',
                        color: category.parent > 0 ? '#1976d2' : '#7b1fa2',
                        fontWeight: '500',
                        fontSize: '12px'
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Countries */}
          {countries.length > 0 && (
            <div className="details drawer-section">
              <SectionHeader icon={Public} title="Countries" count={countries.length} />
              <div className="card drawer-card">
                <div className="chip-container">
                  {countries.map((country, index) => (
                    <Chip
                      key={country.id || index}
                      label={`${country.name || 'N/A'} (${country.iso || 'N/A'})`}
                      size="small"
                      sx={{
                        backgroundColor: '#e8f5e9',
                        color: '#2e7d32',
                        fontWeight: '500',
                        fontSize: '12px'
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Additional Information */}
          <div className="details drawer-section">
            <SectionHeader icon={Info} title="Additional Information" />
            <div className="card drawer-card">
              <InfoRow 
                label="Status" 
                value={coupon.status}
                isBadge={coupon.status === 'draft'}
                badgeComponent={coupon.status === 'draft' ? renderStatusBadge(coupon.status, 'status') : null}
              />
              <InfoRow 
                label="Step" 
                value={coupon.step?.toString()}
              />
              <InfoRow 
                label="Created At" 
                value={formatDate(coupon.created_at)}
              />
              <InfoRow 
                label="Updated At" 
                value={formatDate(coupon.updated_at)}
              />
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ViewCouponLeadDrawer;
